import { GLOBAL_SEARCH_PATH, GLOBAL_SEARCH_TYPES } from "./globalSearchHelper";

export const ADVANCED_SEARCH_MODE = "advanced";
export const CLIENT_ADVANCED_SEARCH_PATH = "/client-search/advanced";
export const SEARCH_ROLES = Object.freeze({ COORDINATOR: "coordinator", COCO: "coco" });

export const BRAZILIAN_STATES = Object.freeze([
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA",
  "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
]);

// Full contract (backend + mock accept MARIANA_2026).
export const ADVANCED_CASE_TABS = Object.freeze(["ALL", "BHP", "ARD", "MARIANA_2026"]);
// Product rule: Mariana is not offered in the UI yet; unknown/hidden values parse as ALL.
export const VISIBLE_ADVANCED_CASE_TABS = Object.freeze(["ALL", "BHP", "ARD"]);
export const ADVANCED_STATUSES = Object.freeze(["ACTIVE", "DISCONTINUANCE", "TERMINATED", "ALL"]);
export const ADVANCED_CLIENT_TYPES = Object.freeze(["ANY", "BUSINESS", "INDIVIDUAL"]);

// Select values that mean "no filter"; they are never sent nor shown as chips.
export const ADVANCED_SELECT_DEFAULTS = Object.freeze({
  caseTab: "ALL",
  status: "ALL",
  clientType: "ANY",
});

const SELECT_OPTIONS = {
  caseTab: VISIBLE_ADVANCED_CASE_TABS,
  status: ADVANCED_STATUSES,
  clientType: ADVANCED_CLIENT_TYPES,
  state: BRAZILIAN_STATES,
};

// Order = grid order and chip order. Keys match the API contract.
export const ADVANCED_FIELDS = Object.freeze([
  "document",
  "name",
  "masterSchedule",
  "matterNumber",
  "email",
  "cocoId",
  "city",
  "state",
  "postalCode",
  "caseTab",
  "status",
  "clientType",
]);

// Display-only companion of cocoId, kept in the URL so chips survive a refresh.
export const COCO_NAME_PARAM = "cocoName";
export const MODE_PARAM = "mode";

const DIGIT_FIELDS = ["document", "postalCode"];

// Indexed fields; searches with none of them (city/state/type/status only) are slow scans.
export const ADVANCED_ANCHOR_FIELDS = Object.freeze([
  "document", "name", "email", "matterNumber", "masterSchedule", "postalCode",
]);

export const hasAdvancedAnchor = (criteria) =>
  ADVANCED_ANCHOR_FIELDS.some((field) => String(criteria?.[field] ?? "").trim() !== "");

export const getAdvancedFields = (role) =>
  role === SEARCH_ROLES.COCO ? ADVANCED_FIELDS.filter((field) => field !== "cocoId") : ADVANCED_FIELDS;

const digitsOf = (value) => String(value ?? "").replace(/\D/g, "");
const countLetters = (value) => (String(value).match(/\p{L}/gu) || []).length;

export const emptyAdvancedCriteria = () => ({
  ...Object.fromEntries(ADVANCED_FIELDS.map((field) => [field, ""])),
  ...ADVANCED_SELECT_DEFAULTS,
  [COCO_NAME_PARAM]: "",
});

// Progressive CPF (000.000.000-00) or, beyond 11 digits, CNPJ (00.000.000/0000-00).
export const maskDocumentInput = (value) => {
  const digits = digitsOf(value).slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
  }
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5");
};

export const maskPostalCodeInput = (value) => {
  const digits = digitsOf(value).slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
};

const normalizeField = (field, rawValue) => {
  const value = String(rawValue ?? "").trim();
  if (!value) return "";
  if (field === "document") return maskDocumentInput(value);
  if (field === "postalCode") return maskPostalCodeInput(value);
  if (field === "state") {
    const upper = value.toUpperCase();
    return BRAZILIAN_STATES.includes(upper) ? upper : "";
  }
  if (SELECT_OPTIONS[field]) {
    const upper = value.toUpperCase();
    return SELECT_OPTIONS[field].includes(upper) ? upper : ADVANCED_SELECT_DEFAULTS[field];
  }
  return value;
};

export const isAdvancedFieldActive = (field, value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return false;
  if (ADVANCED_SELECT_DEFAULTS[field] !== undefined) return trimmed !== ADVANCED_SELECT_DEFAULTS[field];
  if (DIGIT_FIELDS.includes(field)) return digitsOf(trimmed).length > 0;
  return true;
};

// [{ field, value }] for every filled / non-default criterion, in grid order.
export const getActiveCriteria = (criteria, role) =>
  getAdvancedFields(role)
    .filter((field) => isAdvancedFieldActive(field, criteria?.[field]))
    .map((field) => ({ field, value: String(criteria[field]).trim() }));

export const hasActiveCriteria = (criteria, role) => getActiveCriteria(criteria, role).length > 0;

export const hasAdvancedParams = (searchParams) =>
  searchParams.get(MODE_PARAM) === ADVANCED_SEARCH_MODE
  || ADVANCED_FIELDS.some((field) => searchParams.has(field));

export const parseAdvancedCriteria = (searchParams, role) => {
  const criteria = emptyAdvancedCriteria();
  getAdvancedFields(role).forEach((field) => {
    if (searchParams.has(field)) {
      const normalized = normalizeField(field, searchParams.get(field));
      if (normalized) criteria[field] = normalized;
    }
  });
  if (criteria.cocoId) criteria[COCO_NAME_PARAM] = searchParams.get(COCO_NAME_PARAM) || "";
  return criteria;
};

// URL query object: mode=advanced plus only the active criteria.
export const serializeAdvancedCriteria = (criteria, role) => {
  const params = { [MODE_PARAM]: ADVANCED_SEARCH_MODE };
  getActiveCriteria(criteria, role).forEach(({ field, value }) => {
    params[field] = value;
  });
  if (params.cocoId && criteria?.[COCO_NAME_PARAM]) params[COCO_NAME_PARAM] = criteria[COCO_NAME_PARAM];
  return params;
};

export const buildAdvancedSearchUrl = (criteria, role = SEARCH_ROLES.COORDINATOR) => {
  const basePath = role === SEARCH_ROLES.COCO ? CLIENT_ADVANCED_SEARCH_PATH : GLOBAL_SEARCH_PATH;
  return `${basePath}?${new URLSearchParams(serializeAdvancedCriteria(criteria, role)).toString()}`;
};

// Body for POST /clients/lookup (ADVANCED). CoCos never send cocoId: the
// backend scopes them to their own clients.
// Backend contract: coordinators search every client (scope ALL); CoCos only their
// own (scope OWN; the backend ignores scope/cocoId for non-coordinators anyway).
export const ADVANCED_SEARCH_LIMIT = 200;

export const buildAdvancedSearchPayload = (criteria, role = SEARCH_ROLES.COORDINATOR) => ({
  searchMode: "ADVANCED",
  criteria: Object.fromEntries(
    getActiveCriteria(criteria, role).map(({ field, value }) => [
      field,
      DIGIT_FIELDS.includes(field) ? digitsOf(value) : value,
    ]),
  ),
  scope: role === SEARCH_ROLES.COCO ? "OWN" : "ALL",
  limit: ADVANCED_SEARCH_LIMIT,
});

export const ADVANCED_MIN_LENGTHS = Object.freeze({
  document: 3,
  name: 2,
  masterSchedule: 3,
  matterNumber: 3,
  email: 3,
  city: 2,
  postalCode: 3,
});

// { field: translationKeySuffix } for every invalid active field.
export const validateAdvancedCriteria = (criteria, role) => {
  const errors = {};
  getActiveCriteria(criteria, role).forEach(({ field, value }) => {
    const min = ADVANCED_MIN_LENGTHS[field];
    if (!min) return;
    if (DIGIT_FIELDS.includes(field)) {
      if (digitsOf(value).length < min) errors[field] = "minDigits";
    } else if (field === "matterNumber") {
      if (digitsOf(value).length < min) errors[field] = "minDigits";
    } else if (field === "name" || field === "city") {
      if (countLetters(value) < min) errors[field] = "minLetters";
    } else if (value.length < min) {
      errors[field] = "minChars";
    }
  });
  return errors;
};

// Every provided criterion matched every result (AND), so each one is
// highlighted in its column.
export const getAdvancedHighlightQueries = (criteria) => ({
  [GLOBAL_SEARCH_TYPES.NAME]: criteria?.name || "",
  [GLOBAL_SEARCH_TYPES.CPF]: criteria?.document || "",
  [GLOBAL_SEARCH_TYPES.CNPJ]: criteria?.document || "",
  [GLOBAL_SEARCH_TYPES.EMAIL]: criteria?.email || "",
  [GLOBAL_SEARCH_TYPES.MATTER]: criteria?.matterNumber || "",
  [GLOBAL_SEARCH_TYPES.POSTCODE]: criteria?.postalCode || "",
  CITY: criteria?.city || "",
  MASTER_SCHEDULE: criteria?.masterSchedule || "",
});

export const buildCoCoClientRecordPath = (result) =>
  result?.matterId ? `/client-details-matter/${encodeURIComponent(result.matterId)}` : null;
